/* eslint-disable max-lines-per-function */
import React from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Button } from "components/haven-design/buttons";
import { MoveInModal } from "pages/dashboard/interests-profile-slideout/components/modals/move-in";
import {
  ChangeInterestStatus,
  ChangeInterestStatusVariables,
  RelatedInterestActions,
  RelatedInterestActionsVariables,
  tagEligible,
  tagEligibleVariables,
  skipInterestAction,
  skipInterestActionVariables,
} from "types/gql-types";
import logger from "services/logger";
import { useSnackbar } from "components/haven-design/snackbar";
import { InterestStatus, statusFromStage } from "model/interest/status";
import { CHANGE_STATUS, QUERY, TAG_ELIGIBILITY, SKIP_INTEREST } from "./actions-query";
import ReasonModal, { BulkAction } from "./reason-modal";

type Props = {
  currentInterestId: string;
  currentListId: string;
  currentStatus: string;
}

const displayButtonName = (action: BulkAction) => {
  switch (action) {
    case "Moved In":
      return "Move In";
    case "On Waitlist":
      return "Reinstate";
    case "Removed":
      return "Remove";
    case "Star":
      return "Star";
    case "Unstar":
      return "Unstar";
    case "Skip":
      return "Skip";
    default:
      return "";
  }
};

const ACTIONS: BulkAction[] = [
  BulkAction.Star,
  BulkAction.Unstar,
  BulkAction.MovedIn,
  BulkAction.Removed,
  BulkAction.OnWaitlist,
  BulkAction.Skip,
];

const MoveButtons: React.FC<Props> = (props) => {
  const { openSnackbar } = useSnackbar();
  const { currentInterestId, currentListId, currentStatus } = props;

  // Local state for the confirmation modal displayed when attempting to
  // change the interest status.
  const [confirming, setConfirming] = React.useState<BulkAction | null>(null);
  const [reason, setReason] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  // GraphQL queries and mutations.
  const variables = { interestID: currentInterestId, interestListID: currentListId };

  const [skipInterest, skipInterestQuery] = useMutation<
  skipInterestAction,
  skipInterestActionVariables
  >(SKIP_INTEREST);

  const query = useQuery<RelatedInterestActions, RelatedInterestActionsVariables>(QUERY, {
    variables,
  });

  const [setStatus, setStatusQuery] = useMutation<
  ChangeInterestStatus,
  ChangeInterestStatusVariables
  >(CHANGE_STATUS, { refetchQueries: ["activityFeed", "getActivityFeed"] });

  const [setStarred, setStarredQuery] = useMutation<tagEligible, tagEligibleVariables>(
    TAG_ELIGIBILITY,
  );

  if (query.error || query.loading || !query.data || query.data.interest === null) {
    return null;
  }

  const { interest } = query.data;


  const onResetConfirmation = () => {
    setConfirming(null);
    setReason("");
  };

  const isActionVisible = (action: BulkAction): boolean => {
    const status = statusFromStage(interest.status.name);
    switch (action) {
      case BulkAction.MovedIn:
        return status === InterestStatus.Active;
      case BulkAction.Removed:
        return status === InterestStatus.Active;
      case BulkAction.Skip:
        return status === InterestStatus.Active;
      case BulkAction.OnWaitlist:
        return status === InterestStatus.Removed || status === InterestStatus.MovedIn;
      default:
        return true;
    }
  };

  const isActionDisabled = (action: BulkAction): boolean => {
    switch (action) {
      case BulkAction.Star:
      case BulkAction.Unstar:
        return setStarredQuery.loading;
      case BulkAction.Removed:
      case BulkAction.OnWaitlist:
      case BulkAction.MovedIn:
        return setStatusQuery.loading;
      case BulkAction.Skip:
        return skipInterestQuery.loading;
      default:
        return false;
    }
  };

  const onSubmitNewActivity = () => {
    if (confirming === null) {
      return;
    }
    skipInterest({
      variables: {
        data: {
          resourceId: interest.id,
          note: reason || null,
        },
      },
      refetchQueries: ["QueryInterestProfile"],
    })
      .then(() => {
        openSnackbar({ message: "Success", status: "success", duration: 5000 });
        onResetConfirmation();
      })
      .then(() => setIsSubmitting(false))
      .catch((err) => {
        logger.sendError(err);
        onResetConfirmation();
        openSnackbar({ message: "There was a problem skipping this applicant.", status: "error", duration: 5000 });
        setIsSubmitting(false);
      });
  };

  const onStatusChange = () => {
    if (confirming === null) {
      return;
    }

    const statusVariables = {
      interestId: currentInterestId,
      status: confirming,
      reason,
    };

    const request = setStatus({ variables: statusVariables });
    request
      .then(() => query.refetch())
      .then(onResetConfirmation)
      .then(() => setIsSubmitting(false))
      .then(() => openSnackbar({ message: "Updated application status.", status: "success", duration: 5000 }));

    request.catch((err) => {
      logger.sendError(err);
      onResetConfirmation();
      setIsSubmitting(false);
      openSnackbar({ message: "There was a problem updating the application status.", status: "error", duration: 5000 });
    });
  };

  const onModalSubmit = () => {
    setIsSubmitting(true);

    if (confirming === "Skip") {
      onSubmitNewActivity();
    } else {
      onStatusChange();
    }
  };

  return (
    <>
      {ACTIONS.filter(isActionVisible).map((action) => (
        <Button
          key={action}
          color="primary"
          size="L"
          disabled={isActionDisabled(action)}
          className="btn btn-primary btn-sm"
          style={{ marginLeft: 8 }}
          onClick={() => {
            switch (action) {
              case "Star":
              case "Unstar": {
                const eligibility = action === "Star";
                const starVariables = { interestId: currentInterestId, isEligible: eligibility };
                const message = eligibility ? "Starred application" : "Unstarred application";
                const request = setStarred({ variables: starVariables });
                request.then(() => openSnackbar({ message, status: "success", duration: 2500 }));
                request.catch((err) => {
                  logger.sendError(err);
                  openSnackbar({ message: "There was a problem performing this operation.", status: "error" });
                });
                break;
              }

              case "Moved In":
              case "Removed":
              case "Skip":
              case "On Waitlist": {
                setConfirming(action);
                break;
              }

              default:
                break;
            }
          }}
        >
          {displayButtonName(action)}
        </Button>
      ))}

      <MoveInModal
        isOpen={confirming === "Moved In"}
        onRequestClose={() => onResetConfirmation()}
        interestIds={[currentInterestId]}
        interestListId={currentListId}
      />

      <ReasonModal
        isOpen={confirming !== null && confirming !== "Moved In"}
        newStatus={confirming}
        currentStatus={currentStatus as BulkAction}
        onSubmit={() => onModalSubmit()}
        onChange={(newReason) => setReason(newReason)}
        reason={reason}
        onRequestClose={() => onResetConfirmation()}
        onCancel={() => onResetConfirmation()}
        isSubmitting={isSubmitting}
      />
    </>
  );
};

export default MoveButtons;
